import {type ThunkAction, type ThunkDispatch, type UnknownAction} from '@reduxjs/toolkit';

import {useRef} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {newVehicleApi} from '@dms/api/new-vehicle';

import {handleCatalogueError} from './catalogueUi';

const {getColourUpholsteryRestrictions, listTrim, listUpholstery} = newVehicleApi.endpoints;

export type CatalogueState = Parameters<ReturnType<typeof listTrim.select>>[0];

type ReadOptions = {subscribe: boolean; forceRefetch: boolean};

type Endpoint<Arg, Data> = {
  select: (arg: Arg) => (state: CatalogueState) => {isLoading: boolean};
  initiate: (
    arg: Arg,
    options: ReadOptions
  ) => ThunkAction<
    PromiseLike<{isLoading: boolean}> & {unwrap: () => Promise<Data>},
    CatalogueState,
    unknown,
    UnknownAction
  >;
};

// Reads only to build or settle a save; the page's own queries keep the entries alive.
const READ_FROM_SERVER: ReadOptions = {subscribe: false, forceRefetch: true};

/**
 * Each matrix tile replaces a whole set under a version, so two saves in flight would carry the same
 * version and the second would be rejected as stale. Saves run one at a time and read the server state
 * when they start, not when their tile was clicked. A failed read stops a save before its PUT, so a
 * stale set is never written; each save ends with a read so its tile settles on what the server kept.
 */
export function useMatrixSaveQueue(modelYearId: string) {
  const store = useStore<CatalogueState>();
  const dispatch = useDispatch<ThunkDispatch<CatalogueState, unknown, UnknownAction>>();
  const queue = useRef<Promise<void>>(Promise.resolve());
  const enqueueSave = (save: () => Promise<void>) => {
    queue.current = queue.current.then(() => save().catch(handleCatalogueError));
    return queue.current;
  };
  const nextStoreUpdate = () =>
    new Promise<void>((resolve) => {
      const unsubscribe = store.subscribe(() => {
        unsubscribe();
        resolve();
      });
    });
  // A forced read joins a request already in flight, which may predate the last save, and when RTK
  // starts a delayed refetch as an older request ends it returns the cache without waiting. Wait until
  // nothing is in flight for the entry, then force a read in the same tick so it sees every save. Its
  // result is read back from the cache: if a refetch took the entry over as it ended, that result says
  // nothing about this read (it may have failed), so read again.
  const readFresh = async <Arg, Data>(endpoint: Endpoint<Arg, Data>, arg: Arg): Promise<Data> => {
    while (endpoint.select(arg)(store.getState()).isLoading) {
      await nextStoreUpdate();
    }
    const read = dispatch(endpoint.initiate(arg, READ_FROM_SERVER));
    return (await read).isLoading ? readFresh(endpoint, arg) : read.unwrap();
  };
  return {
    enqueueSave,
    readRestrictions: () => readFresh(getColourUpholsteryRestrictions, {modelYearId}),
    readTrimUpholstery: () => Promise.all([readFresh(listTrim, {}), readFresh(listUpholstery, {})]),
  };
}
